import { FuelPlanCalculator } from "types";

export const SAFETY_BUFFER_GALLONS = 30;
export const MINIMUM_PURCHASE_GALLONS = 50;
export const DEFAULT_AVERAGE_MPG = 6.5;
export const POUNDS_PER_GALLON = 7.1;

export type CalculatorState = {
  currentFuelRangeMiles: number;
  currentFuelGallons: number;
  milesToNextStop: number;
  averageMpg: number;
  targetGallons: number;
};

export type CalculatorInputState = {
  currentFuelRangeMiles: string;
  milesToNextStop: string;
  averageMpg: string;
};

export type CalculatorInputField = "milesToNextStop" | "averageMpg";

export type InitialCalculatorState = {
  values: CalculatorState;
  inputs: CalculatorInputState;
};

type CalculatorValues = Omit<CalculatorState, "targetGallons">;

export const EMPTY_CALCULATOR_STATE: CalculatorState = {
  currentFuelRangeMiles: 0,
  currentFuelGallons: 0,
  milesToNextStop: 0,
  averageMpg: DEFAULT_AVERAGE_MPG,
  targetGallons: MINIMUM_PURCHASE_GALLONS,
};

export const EMPTY_CALCULATOR_INPUTS: CalculatorInputState = {
  currentFuelRangeMiles: "0",
  milesToNextStop: "0",
  averageMpg: DEFAULT_AVERAGE_MPG.toString(),
};

export const parseCalculatorInput = (value: string): number => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const formatCalculatorInput = (value: number): string =>
  Number.isFinite(value) ? value.toString() : "";

export const isValidCalculatorInput = (value: string): boolean =>
  /^\d*(\.\d*)?$/.test(value);

export const calculateTargetGallons = (values: CalculatorValues): number => {
  const averageMpg = values.averageMpg > 0 ? values.averageMpg : 1;
  const gallonsNeeded = Math.ceil(
    Math.max(
      0,
      values.milesToNextStop / averageMpg +
        SAFETY_BUFFER_GALLONS -
        values.currentFuelGallons
    )
  );

  return Math.max(MINIMUM_PURCHASE_GALLONS, gallonsNeeded);
};

export const createInitialCalculatorState = (
  defaults: FuelPlanCalculator | null,
  milesToNextStop: number,
  hasSelectedLeg: boolean
): InitialCalculatorState => {
  const valuesWithoutTarget: CalculatorValues = {
    currentFuelRangeMiles: defaults?.currentFuelRangeMiles ?? 0,
    currentFuelGallons: defaults?.currentFuelGallons ?? 0,
    milesToNextStop: hasSelectedLeg ? milesToNextStop : 0,
    averageMpg:
      defaults?.averageMpg ?? (hasSelectedLeg ? DEFAULT_AVERAGE_MPG : 0),
  };

  return {
    values: {
      ...valuesWithoutTarget,
      targetGallons: hasSelectedLeg
        ? calculateTargetGallons(valuesWithoutTarget)
        : 0,
    },
    inputs: {
      currentFuelRangeMiles: formatCalculatorInput(
        valuesWithoutTarget.currentFuelRangeMiles
      ),
      milesToNextStop: hasSelectedLeg
        ? formatCalculatorInput(valuesWithoutTarget.milesToNextStop)
        : "",
      averageMpg: formatCalculatorInput(valuesWithoutTarget.averageMpg),
    },
  };
};

export const updateCalculatorLegMiles = (
  current: CalculatorState,
  milesToNextStop: number
): CalculatorState => {
  const updatedValues: CalculatorValues = {
    currentFuelRangeMiles: current.currentFuelRangeMiles,
    currentFuelGallons: current.currentFuelGallons,
    milesToNextStop,
    averageMpg: current.averageMpg,
  };

  return {
    ...updatedValues,
    targetGallons: calculateTargetGallons(updatedValues),
  };
};

export const updateAvailableFuelRange = (
  current: CalculatorState,
  currentFuelRangeMiles: number
): CalculatorState => ({
  ...current,
  currentFuelRangeMiles,
  currentFuelGallons:
    current.averageMpg > 0
      ? Math.round((currentFuelRangeMiles / current.averageMpg) * 10) / 10
      : 0,
});

export const calculateTargetForCurrentValues = (
  current: CalculatorState
): CalculatorState => ({
  ...current,
  targetGallons: calculateTargetGallons(current),
});
